import { notification, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import { JobPostingDetail, RecruitmentProcessesActionsRejectAllCollectionData } from '../types';
import { buildRecruitmentProcessQueryParam } from '../utils';
import RejectCandidateModal, {
  RejectCandidateFormValues,
  RejectCandidateModalProps,
} from './RejectCandidateModal';

interface BulkRejectCandidateModalProps extends Omit<RejectCandidateModalProps, 'helmetTitle'> {
  jobPostingId: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: RejectCandidateFormValues,
    context: unknown
  ) => void | Promise<unknown>;
  functionId?: JobPostingDetail['function_id'];
  totalCount?: number;
}

const BulkRejectCandidateModal = ({
  jobPostingId,
  onSuccess,
  okButtonProps,
  totalCount,
  functionId,
  ...props
}: BulkRejectCandidateModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const mutation = useAPIMutation(
    async (
      {
        annotation,
        message,
        sendFeedback,
        attachments,
        fromName,
        subject,
        enableReplyTo,
        replyTo,
      }: RejectCandidateFormValues,
      api
    ) => {
      const eliminateCandidateAnnotation =
        t('Esta pessoa foi reprovada em uma ação em massa. Justificativa: ') + annotation;

      const emailData: Omit<RecruitmentProcessesActionsRejectAllCollectionData, 'commentary'> = {
        from_name: fromName,
        subject,
        message,
        ...(enableReplyTo ? { reply_to: replyTo } : {}),
      };

      if (sendFeedback) {
        const attachmentIds: number[] = [];

        for (const attachment of attachments || []) {
          if (attachment.originFileObj) {
            const attachmentFormData = new FormData();
            attachmentFormData.append('filename', attachment.name);
            attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
            attachmentFormData.append('mimetype', attachment.type as string);
            const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            attachmentIds.push(attachmentResponse.data.id);
          } else {
            attachmentIds.push(parseInt(attachment.uid, 10));
          }
        }

        emailData['attachments'] = attachmentIds;
      }

      await api.post(
        '/recruitment-processes-actions/reject-all/',
        {
          commentary: eliminateCandidateAnnotation,
          ...(sendFeedback ? emailData : {}),
        },
        {
          params: await buildRecruitmentProcessQueryParam(
            searchParams,
            api,
            jobPostingId,
            functionId
          ),
        }
      );
    },
    {
      onSuccess,
      onSettled: async (_, __) => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries('/candidate-history/');
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.error === 'empty_queryset_reject_all') {
          return notification.error({
            duration: 0,
            message: t('Erro'),
            description: t(
              'Não é possível realizar esta ação, pois não há candidatos a serem selecionados. Recarregue a página e tente novamente.'
            ),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  return (
    <RejectCandidateModal
      helmetTitle={t('Reprovar {{totalCount}} pessoa(s)', { totalCount: totalCount || '...' })}
      title={
        <Typography.Text>
          <Trans>Reprovar</Trans>{' '}
          <Typography.Text strong>
            {totalCount || '...'} <Trans>pessoa(s)</Trans>
          </Typography.Text>
        </Typography.Text>
      }
      loading={mutation.isLoading}
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      warningParagraph={
        <Typography.Paragraph>
          <Trans>Ao confirmar,</Trans>{' '}
          <Typography.Text strong>
            {totalCount || '...'} <Trans>pessoas</Trans>
          </Typography.Text>{' '}
          <Trans>serão reprovadas no processo seletivo.</Trans>
        </Typography.Paragraph>
      }
      {...props}
    />
  );
};

export default BulkRejectCandidateModal;
